// src/pages/public/Checkout.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, push, update } from 'firebase/database';
import { initializePayment } from '../services/paystackService';
import Swal from 'sweetalert2';
import {
  ShoppingCartIcon, UserIcon, MapPinIcon, PhoneIcon,
  EnvelopeIcon, ChevronRightIcon, ChevronLeftIcon,
  CheckCircleIcon, XMarkIcon, TruckIcon, ShieldCheckIcon,
  ClockIcon, TagIcon, SparklesIcon, DocumentTextIcon,
  ChatBubbleLeftRightIcon, CreditCardIcon, InformationCircleIcon,
  BuildingOfficeIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolid,
  ShoppingBagIcon as ShoppingBagSolid,
} from '@heroicons/react/24/solid';

const WHATSAPP_NUMBER = '254748798287';

// WhatsApp Icon
const WhatsAppIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2800, toast: true, position: 'top-end', showConfirmButton: false });

const LS = {
  get: (key, fb = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
  remove: (key) => { try { localStorage.removeItem(key); } catch {} },
};

const StepBar = ({ step }) => {
  const steps = ['Billing', 'Delivery', 'Payment'];
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = step > idx;
        const active = step === idx;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                style={{ background: done ? '#16a34a' : active ? '#800020' : '#e5e7eb', color: (done || active) ? '#fff' : '#9ca3af', boxShadow: active ? '0 0 0 4px rgba(128,0,32,0.15)' : 'none' }}>
                {done ? <CheckCircleSolid className="h-5 w-5" /> : idx}
              </div>
              <span className="text-xs font-semibold transition-colors duration-300"
                style={{ color: done ? '#16a34a' : active ? '#800020' : '#9ca3af' }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className="h-0.5 w-16 mx-1 mb-5 rounded transition-all duration-300"
                style={{ background: step > idx ? '#16a34a' : '#e5e7eb' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && (
      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
        <InformationCircleIcon className="h-3.5 w-3.5" />{error}
      </p>
    )}
  </div>
);

const inputCls = (err) =>
  `w-full px-4 py-3 border-2 rounded-xl text-sm transition-all focus:outline-none ${err ? 'border-red-300 focus:border-red-500 bg-red-50' : 'border-gray-200 focus:border-red-800 bg-white'}`;

const OrderMiniList = ({ cart, subtotal, savings }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-24">
    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between"
      style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
      <div className="flex items-center gap-2">
        <ShoppingBagSolid className="h-4 w-4" style={{ color: '#800020' }} />
        <span className="text-sm font-extrabold text-gray-900">Your Order</span>
      </div>
      <span className="text-xs text-gray-400">{cart.reduce((s, i) => s + i.quantity, 0)} items</span>
    </div>
    <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
      {cart.map(item => (
        <div key={item.id} className="flex items-center gap-3 px-5 py-3">
          <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" /> : <ShoppingCartIcon className="h-5 w-5 text-gray-300" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-800 truncate">{item.name}</p>
            <p className="text-[10px] text-gray-400">Qty: {item.quantity}</p>
          </div>
          <p className="text-xs font-bold flex-shrink-0" style={{ color: '#800020' }}>KES {(item.price * item.quantity)?.toLocaleString()}</p>
        </div>
      ))}
    </div>
    <div className="px-5 py-4 border-t border-gray-100 space-y-2" style={{ background: '#fafafa' }}>
      {savings > 0 && (
        <div className="flex justify-between text-xs">
          <span className="text-green-600 font-semibold">Savings</span>
          <span className="font-bold text-green-600">- KES {savings?.toLocaleString()}</span>
        </div>
      )}
      <div className="flex justify-between items-center pt-1">
        <span className="text-base font-extrabold text-gray-900">Total</span>
        <span className="text-2xl font-extrabold" style={{ color: '#800020' }}>KES {subtotal?.toLocaleString()}</span>
      </div>
    </div>
  </div>
);

const Checkout = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();

  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [step, setStep] = useState(1);

  const handleNavigateToCart = () => navigate('/cart');
  const handleNavigateToShop = () => navigate('/shop');

  const [billing, setBilling] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
  });
  const [billingErrors, setBillingErrors] = useState({});

  const [delivery, setDelivery] = useState({
    address: '',
    area: '',
    city: 'Nairobi',
    landmark: '',
    notes: '',
    contactPref: 'phone',
  });
  const [deliveryErrors, setDeliveryErrors] = useState({});

  const [payMethod, setPayMethod] = useState('whatsapp_cod');

  useEffect(() => {
    if (userData?.personalInfo) {
      const info = userData.personalInfo;
      const nameParts = (info.name || '').split(' ');
      setBilling(b => ({
        ...b,
        firstName: nameParts[0] || b.firstName,
        lastName: nameParts.slice(1).join(' ') || b.lastName,
        email: info.email || b.email,
        phone: info.phone || b.phone,
      }));
    } else if (currentUser?.email) {
      setBilling(b => ({ ...b, email: currentUser.email }));
    }
  }, [userData, currentUser]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (currentUser) {
          const snap = await get(ref(database, `users/${currentUser.uid}/cart`));
          const fbCart = snap.exists() ? (Array.isArray(snap.val()) ? snap.val() : Object.values(snap.val())) : [];
          setCart(fbCart);
        } else {
          setCart(LS.get('shop_cart', []));
        }
      } catch {
        setCart(LS.get('shop_cart', []));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const savings = cart.reduce((s, i) => s + ((i.originalPrice || i.price) - i.price) * i.quantity, 0);

  const validateBilling = () => {
    const errs = {};
    if (!billing.firstName.trim()) errs.firstName = 'First name is required';
    if (!billing.lastName.trim()) errs.lastName = 'Last name is required';
    if (!billing.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email)) errs.email = 'Valid email is required';
    if (!billing.phone.trim() || billing.phone.trim().length < 9) errs.phone = 'Valid phone number is required';
    setBillingErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateDelivery = () => {
    const errs = {};
    if (!delivery.address.trim()) errs.address = 'Street address is required';
    if (!delivery.area.trim()) errs.area = 'Area/estate is required';
    if (!delivery.city.trim()) errs.city = 'City is required';
    setDeliveryErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    if (step === 1 && !validateBilling()) return;
    if (step === 2 && !validateDelivery()) return;
    setStep(s => Math.min(s + 1, 3));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    if (step === 1) {
      handleNavigateToCart();
    } else {
      setStep(s => Math.max(s - 1, 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const buildOrder = (orderNumber) => ({
    orderNumber,
    userId: currentUser?.uid || null,
    userInfo: {
      name: `${billing.firstName} ${billing.lastName}`.trim(),
      email: billing.email,
      phone: billing.phone,
      company: billing.company || '',
    },
    deliveryInfo: {
      address: delivery.address,
      area: delivery.area,
      city: delivery.city,
      landmark: delivery.landmark || '',
      notes: delivery.notes || '',
      contactPref: delivery.contactPref,
    },
    items: cart.map(item => ({
      productId: item.id,
      name: item.name,
      quantity: item.quantity,
      price: item.price,
      originalPrice: item.originalPrice || item.price,
      subtotal: item.price * item.quantity,
      image: item.image || '',
    })),
    subtotal,
    savings,
    tax: 0,
    total: subtotal,
    paymentMethod: payMethod,
    paymentReference: '',
    paymentStatus: 'pending',
    orderStatus: 'pending',
    orderSource: payMethod === 'whatsapp_cod' ? 'shop_cart_whatsapp' : 'shop_cart_online',
    createdAt: Date.now(),
  });

  const generateOrderNumber = async () => {
    try {
      const counterRef = ref(database, 'system/counters/orderNumber');
      const snap = await get(counterRef);
      const next = (snap.exists() ? snap.val() : 1000) + 1;
      await set(counterRef, next);
      return `ORD-${next}`;
    } catch {
      return `ORD-${Date.now()}`;
    }
  };

  const clearCart = async () => {
    if (currentUser) {
      try {
        await set(ref(database, `users/${currentUser.uid}/cart`), []);
      } catch { }
    } else {
      LS.remove('shop_cart');
    }
    setCart([]);
  };

  const buildWhatsAppMessage = (orderNumber) => {
    const customerName = `${billing.firstName} ${billing.lastName}`.trim();
    const itemLines = cart.map(i => `  • ${i.name} x${i.quantity} — KES ${(i.price * i.quantity).toLocaleString()}`).join('%0A');
    return (
      `🛒 *NEW ORDER: ${orderNumber}*%0A` +
      `══════════════════════%0A` +
      `👤 *Customer:* ${customerName}%0A` +
      `📞 *Phone:* ${billing.phone}%0A` +
      `📧 *Email:* ${billing.email}%0A` +
      `${billing.company ? `🏢 *Company:* ${billing.company}%0A` : ''}` +
      `%0A` +
      `📦 *Items:*%0A${itemLines}%0A` +
      `%0A` +
      `💰 *Total: KES ${subtotal.toLocaleString()}*${savings > 0 ? ` _(saved KES ${savings.toLocaleString()})_` : ''}%0A` +
      `%0A` +
      `📍 *Delivery Address:*%0A` +
      `  ${delivery.address}, ${delivery.area}%0A` +
      `  ${delivery.city}%0A` +
      `${delivery.landmark ? `  Landmark: ${delivery.landmark}%0A` : ''}` +
      `${delivery.notes ? `  Notes: ${delivery.notes}%0A` : ''}` +
      `%0A` +
      `📲 *Preferred contact:* ${delivery.contactPref === 'phone' ? `Phone (${billing.phone})` : `Email (${billing.email})`}%0A` +
      `%0A` +
      `_Payment: Cash on Delivery / M-Pesa_`
    );
  };

  const placeWhatsAppOrder = async () => {
    setPlacing(true);
    try {
      const orderNumber = await generateOrderNumber();
      const order = buildOrder(orderNumber);

      const orderRef = push(ref(database, 'orders'));
      await set(orderRef, { ...order, id: orderRef.key });

      if (currentUser) {
        await set(ref(database, `users/${currentUser.uid}/recentOrders/${orderRef.key}`), {
          orderId: orderRef.key,
          orderNumber,
          total: subtotal,
          status: 'pending',
          orderSource: 'shop_cart_whatsapp',
          date: Date.now(),
        });
      }

      for (const item of cart) {
        try {
          const pRef = ref(database, `products/${item.id}`);
          const pSnap = await get(pRef);
          if (pSnap.exists()) {
            await update(pRef, { totalSales: (pSnap.val().totalSales || 0) + item.quantity });
          }
        } catch { }
      }

      await clearCart();

      const msg = buildWhatsAppMessage(orderNumber);
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');

      await Swal.fire({
        title: '🎉 Order Placed!',
        html: `<div style="text-align:center"><p>Your order <strong>${orderNumber}</strong> has been recorded.</p><p>WhatsApp has opened with your order details.</p></div>`,
        icon: 'success',
        confirmButtonColor: '#800020',
      });

      navigate('/payment-confirmation', { state: { order: { orderNumber, orderId: orderRef.key } } });
    } catch (err) {
      console.error('Order error:', err);
      toast('error', 'Failed to place order', err?.message || 'Please try again');
    } finally {
      setPlacing(false);
    }
  };

  const processPaystackPayment = async () => {
    if (!validateBilling() || !validateDelivery()) {
      setStep(1);
      return;
    }

    setPlacing(true);

    try {
      const orderNumber = await generateOrderNumber();
      const order = buildOrder(orderNumber);

      // Save order with pending status
      const orderRef = push(ref(database, 'orders'));
      await set(orderRef, { ...order, id: orderRef.key, paymentStatus: 'pending', orderStatus: 'pending_payment' });

      const paymentDetails = {
        amount: subtotal,
        type: 'shop',
        planName: `Order ${orderNumber}`,
        email: currentUser?.email || billing.email,
        userId: currentUser?.uid || 'guest',
        customFields: {
          orderId: orderRef.key,
          orderNumber: orderNumber,
          items: cart.map(i => ({ name: i.name, quantity: i.quantity, price: i.price })),
          billingInfo: billing,
          deliveryInfo: delivery,
        },
      };

      const result = await initializePayment(paymentDetails, { email: billing.email, uid: currentUser?.uid });

      if (result.success) {
        sessionStorage.setItem('pendingOrder', JSON.stringify({
          orderRef: orderRef.key,
          orderNumber: orderNumber,
          reference: result.reference,
          cart: cart,
        }));

        window.location.href = result.authorizationUrl;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast('error', 'Payment Error', error.message || 'Unable to process payment');
      setPlacing(false);
    }
  };

  const placeOrder = async () => {
    if (!currentUser) {
      toast('warning', 'Please sign in to place your order');
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }

    if (payMethod === 'whatsapp_cod') {
      await placeWhatsAppOrder();
    } else if (payMethod === 'paystack') {
      await processPaystackPayment();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fa' }}>
        <div className="w-12 h-12 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
      </div>
    );
  }

  if (cart.length === 0 && !placing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4" style={{ background: '#f8f9fa' }}>
        <ShoppingCartIcon className="h-20 w-20 text-gray-200" />
        <h2 className="text-2xl font-bold text-gray-700">Your cart is empty</h2>
        <p className="text-gray-400 text-sm">Looks like you haven't added any items yet</p>
        <button onClick={handleNavigateToShop} className="px-8 py-3 rounded-2xl text-white font-bold text-sm mt-4" style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
          Browse Products
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#f1f3f5 100%)' }}>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handleNavigateToCart}
              className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-800 transition-all">
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900">Checkout</h1>
              <p className="text-xs text-gray-400">Complete your purchase securely</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ShieldCheckIcon className="h-4 w-4 text-green-500" />
            <span className="hidden sm:inline">256-bit SSL Secure</span>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">
        <StepBar step={step} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="billing" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#800020' }}>
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-extrabold text-gray-900">Billing Information</h2>
                      <p className="text-xs text-gray-400">Your personal and contact details</p>
                    </div>
                  </div>
                  <div className="p-6 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label="First Name" required error={billingErrors.firstName}>
                        <input value={billing.firstName} onChange={e => setBilling(b => ({ ...b, firstName: e.target.value }))} placeholder="John" className={inputCls(billingErrors.firstName)} />
                      </Field>
                      <Field label="Last Name" required error={billingErrors.lastName}>
                        <input value={billing.lastName} onChange={e => setBilling(b => ({ ...b, lastName: e.target.value }))} placeholder="Doe" className={inputCls(billingErrors.lastName)} />
                      </Field>
                    </div>
                    <Field label="Company / Organisation">
                      <div className="relative">
                        <BuildingOfficeIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                        <input value={billing.company} onChange={e => setBilling(b => ({ ...b, company: e.target.value }))} placeholder="Optional — company name" className={`${inputCls(false)} pl-10`} />
                      </div>
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label="Email Address" required error={billingErrors.email}>
                        <div className="relative">
                          <EnvelopeIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                          <input value={billing.email} onChange={e => setBilling(b => ({ ...b, email: e.target.value }))} placeholder="john@example.com" type="email" className={`${inputCls(billingErrors.email)} pl-10`} />
                        </div>
                      </Field>
                      <Field label="Phone Number" required error={billingErrors.phone}>
                        <div className="relative">
                          <PhoneIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                          <input value={billing.phone} onChange={e => setBilling(b => ({ ...b, phone: e.target.value }))} placeholder="0712 345 678" type="tel" className={`${inputCls(billingErrors.phone)} pl-10`} />
                        </div>
                      </Field>
                    </div>
                    <Field label="Preferred Contact Method">
                      <div className="grid grid-cols-2 gap-3 mt-0.5">
                        {[
                          { val: 'phone', label: 'Phone / WhatsApp', icon: <PhoneIcon className="h-4 w-4" /> },
                          { val: 'email', label: 'Email', icon: <EnvelopeIcon className="h-4 w-4" /> },
                        ].map(({ val, label, icon }) => (
                          <button key={val} type="button" onClick={() => setDelivery(d => ({ ...d, contactPref: val }))}
                            className="flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all"
                            style={{ borderColor: delivery.contactPref === val ? '#800020' : '#e5e7eb', background: delivery.contactPref === val ? '#fef2f2' : '#fff', color: delivery.contactPref === val ? '#800020' : '#6b7280' }}>
                            {icon}{label}
                            {delivery.contactPref === val && <CheckCircleSolid className="h-4 w-4 ml-auto" style={{ color: '#800020' }} />}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="delivery" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#800020' }}>
                      <MapPinIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-extrabold text-gray-900">Delivery Address</h2>
                      <p className="text-xs text-gray-400">Where should we deliver your order?</p>
                    </div>
                  </div>
                  <div className="p-6 space-y-5">
                    <Field label="Street Address" required error={deliveryErrors.address}>
                      <div className="relative">
                        <MapPinIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                        <input value={delivery.address} onChange={e => setDelivery(d => ({ ...d, address: e.target.value }))} placeholder="e.g., 24 Ngong Road, Apartment 5B" className={`${inputCls(deliveryErrors.address)} pl-10`} />
                      </div>
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label="Area / Estate" required error={deliveryErrors.area}>
                        <input value={delivery.area} onChange={e => setDelivery(d => ({ ...d, area: e.target.value }))} placeholder="e.g., Westlands, Karen, Kilimani" className={inputCls(deliveryErrors.area)} />
                      </Field>
                      <Field label="City / Town" required error={deliveryErrors.city}>
                        <input value={delivery.city} onChange={e => setDelivery(d => ({ ...d, city: e.target.value }))} placeholder="Nairobi" className={inputCls(deliveryErrors.city)} />
                      </Field>
                    </div>
                    <Field label="Landmark / Directions">
                      <div className="relative">
                        <SparklesIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                        <input value={delivery.landmark} onChange={e => setDelivery(d => ({ ...d, landmark: e.target.value }))} placeholder="e.g., Near Sarit Centre, opposite Total" className={`${inputCls(false)} pl-10`} />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Help our delivery team find you easily</p>
                    </Field>
                    <Field label="Delivery Notes">
                      <div className="relative">
                        <DocumentTextIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                        <textarea value={delivery.notes} onChange={e => setDelivery(d => ({ ...d, notes: e.target.value }))} rows={3} placeholder="Any special instructions? e.g., Call before arriving, gate code is 1234…" className={`${inputCls(false)} pl-10 resize-none`} />
                      </div>
                    </Field>
                    <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                      <ClockIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-blue-800">Estimated Delivery</p>
                        <p className="text-xs text-blue-600 mt-0.5">1–3 business days within Nairobi. Delivery cost communicated upon order confirmation.</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="payment" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}>
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-5">
                    <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#800020' }}>
                        <CreditCardIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-extrabold text-gray-900">Payment Method</h2>
                        <p className="text-xs text-gray-400">Choose how you'd like to pay</p>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <button type="button" onClick={() => setPayMethod('whatsapp_cod')}
                        className="w-full flex items-start gap-4 p-5 rounded-2xl border-2 text-left transition-all hover:shadow-md"
                        style={{ borderColor: payMethod === 'whatsapp_cod' ? '#25D366' : '#e5e7eb', background: payMethod === 'whatsapp_cod' ? '#f0fdf4' : '#fff' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#25D366' }}>
                          <WhatsAppIcon className="h-7 w-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <p className="text-base font-extrabold text-gray-900">WhatsApp Order</p>
                            <span className="text-[10px] font-bold text-white px-2 py-1 rounded-full" style={{ background: '#16a34a' }}>RECOMMENDED</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">Pay on delivery (Cash / M-Pesa). Order details sent directly to WhatsApp.</p>
                          <div className="flex items-center gap-2 mt-2">
                            {['💵 Cash', '📱 M-Pesa'].map(m => (
                              <span key={m} className="text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">{m}</span>
                            ))}
                          </div>
                        </div>
                        {payMethod === 'whatsapp_cod' && <CheckCircleSolid className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: '#16a34a' }} />}
                      </button>

                      <button type="button" onClick={() => setPayMethod('paystack')}
                        className="w-full flex items-start gap-4 p-5 rounded-2xl border-2 text-left transition-all hover:shadow-md"
                        style={{ borderColor: payMethod === 'paystack' ? '#800020' : '#e5e7eb', background: payMethod === 'paystack' ? '#fef2f2' : '#fff' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#1a1a2e' }}>
                          <CreditCardIcon className="h-7 w-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-base font-extrabold text-gray-900">Pay Online via Paystack</p>
                          <p className="text-xs text-gray-500 mt-1 leading-relaxed">Secure online payment. Supports Visa, Mastercard & M-Pesa.</p>
                          <div className="flex items-center gap-2 mt-2">
                            {['💳 Visa', '💳 Mastercard', '📱 M-Pesa'].map(m => (
                              <span key={m} className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{m}</span>
                            ))}
                          </div>
                        </div>
                        {payMethod === 'paystack' && <CheckCircleSolid className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: '#800020' }} />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#800020' }}>
                        <CheckCircleIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-extrabold text-gray-900">Review & Confirm</h2>
                        <p className="text-xs text-gray-400">Double-check your details before placing order</p>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex gap-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-20 flex-shrink-0">Contact</span>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{billing.firstName} {billing.lastName}</p>
                          <p className="text-xs text-gray-500">{billing.email} · {billing.phone}</p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-20 flex-shrink-0">Delivery</span>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{delivery.address}</p>
                          <p className="text-xs text-gray-500">{delivery.area}, {delivery.city}</p>
                          {delivery.landmark && <p className="text-xs text-gray-400 italic">Near: {delivery.landmark}</p>}
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-20 flex-shrink-0">Payment</span>
                        <div className="flex items-center gap-2">
                          {payMethod === 'whatsapp_cod'
                            ? <><WhatsAppIcon className="h-4 w-4 text-green-500" /><span className="text-sm font-semibold text-gray-900">WhatsApp / Cash on Delivery</span></>
                            : <><CreditCardIcon className="h-4 w-4 text-gray-500" /><span className="text-sm font-semibold text-gray-900">Paystack (Online Payment)</span></>}
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 leading-relaxed border-t border-gray-100 pt-4 mt-2">
                        By placing this order you agree to our terms of service. For WhatsApp orders, final payment is arranged upon delivery.
                      </p>
                      <motion.button
                        whileHover={!placing ? { scale: 1.02 } : {}}
                        whileTap={!placing ? { scale: 0.98 } : {}}
                        onClick={placeOrder}
                        disabled={placing}
                        className="w-full py-4 rounded-2xl text-white font-extrabold text-base flex items-center justify-center gap-2.5 shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{ background: payMethod === 'whatsapp_cod' ? 'linear-gradient(135deg,#1a5c28,#25D366)' : 'linear-gradient(135deg,#800020,#b30000)' }}>
                        {placing
                          ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing…</>
                          : payMethod === 'whatsapp_cod'
                            ? <><WhatsAppIcon className="h-5 w-5" />Send Order via WhatsApp</>
                            : <><CreditCardIcon className="h-5 w-5" />Pay KES {subtotal.toLocaleString()} via Paystack</>}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center justify-between mt-6">
              <button onClick={goBack}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all">
                <ChevronLeftIcon className="h-4 w-4" />
                {step === 1 ? 'Back to Cart' : 'Back'}
              </button>
              {step < 3 && (
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={goNext}
                  className="inline-flex items-center gap-2 px-7 py-2.5 rounded-xl text-white font-bold text-sm shadow-md transition-all"
                  style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
                  Continue <ChevronRightIcon className="h-4 w-4" />
                </motion.button>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <OrderMiniList cart={cart} subtotal={subtotal} savings={savings} />
            <div className="mt-4 p-4 rounded-2xl" style={{ background: '#f8f9fa', border: '1px dashed #e5e7eb' }}>
              <div className="flex items-start gap-2.5">
                <InformationCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-400 leading-relaxed">
                  {step === 1 && 'Fill in your contact details. We use these to confirm your order.'}
                  {step === 2 && 'Provide your full delivery address including landmarks for easy delivery.'}
                  {step === 3 && 'Choose WhatsApp Order for cash/M-Pesa on delivery, or Paystack for secure online payment.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;